import React, { useState } from 'react'
import { Tabs as FbUITabs } from 'components/FbUI/Tabs'
import FbButton from 'components/FbUI/FbButton'
import { TagCountBadge } from 'components/FbUI/TagCountBadge'
import { TextPill } from 'components/FbUI/TextPill'
import { EmailValidityPill } from 'components/FbUI/EmailValidityPill'
import { FbLink } from 'components/FbUI/FbLink'
import { OverflownText } from 'components/OverflownText'
import { JobStatusPill } from 'components/FbUI/JobStatusPill'
import Checkbox from 'components/Checkbox/checkbox'
import { TreeList } from 'components/UI/TreeList'
import type { TreeNode } from 'components/UI/TreeList'
import { EmailValidity } from 'models/contacts'
import { Preview, Section, Row, Col } from './PreviewKit'

// ---------------------------------------------------------------------------
// FbButton — all variants, all sizes, all states
// ---------------------------------------------------------------------------
function FbButtonPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Variants (size md)">
        <Row>
          <FbButton>Primary</FbButton>
          <FbButton variant="secondary">Secondary</FbButton>
          <FbButton variant="outline">Outline</FbButton>
          <FbButton variant="primary-outline">Primary Outline</FbButton>
          <FbButton variant="warning">Warning</FbButton>
          <FbButton variant="danger">Danger</FbButton>
          <FbButton variant="danger-outline">Danger Outline</FbButton>
          <FbButton variant="transparent">Transparent</FbButton>
        </Row>
      </Section>
      <Section label="Sizes (primary variant)">
        <Row align="center">
          <Col label="xs">
            <FbButton size="xs">xs</FbButton>
          </Col>
          <Col label="sm">
            <FbButton size="sm">sm</FbButton>
          </Col>
          <Col label="md">
            <FbButton size="md">md</FbButton>
          </Col>
          <Col label="mdlg">
            <FbButton size="mdlg">mdlg</FbButton>
          </Col>
          <Col label="lg">
            <FbButton size="lg">lg</FbButton>
          </Col>
        </Row>
      </Section>
      <Section label="States">
        <Row>
          <FbButton loading>Loading</FbButton>
          <FbButton disabled>Disabled</FbButton>
          <FbButton tooltip="This action archives the record permanently">
            With Tooltip ↗
          </FbButton>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// TextPill — custom backgroundColor + textColor combos
// ---------------------------------------------------------------------------
function TextPillPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Color combinations">
        <Row>
          <TextPill backgroundColor="#D1FAE5" textColor="#065F46">
            Active
          </TextPill>
          <TextPill backgroundColor="#FEE2E2" textColor="#991B1B">
            Closed Lost
          </TextPill>
          <TextPill backgroundColor="#E0E7FF" textColor="#3730A3">
            Pending
          </TextPill>
          <TextPill backgroundColor="#FEF3C7" textColor="#92400E">
            Warning
          </TextPill>
          <TextPill backgroundColor="#F3F4F6" textColor="#374151">
            Neutral
          </TextPill>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// TagCountBadge
// ---------------------------------------------------------------------------
function TagCountBadgePreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Count values">
        <Row align="center">
          <Col label="0">
            <TagCountBadge count={0} />
          </Col>
          <Col label="3">
            <TagCountBadge count={3} />
          </Col>
          <Col label="12">
            <TagCountBadge count={12} />
          </Col>
          <Col label="99+">
            <TagCountBadge count={99} />
          </Col>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// EmailValidityPill — all enum values
// ---------------------------------------------------------------------------
function EmailValidityPillPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="All validity states">
        <Row>
          <Col label="VALID">
            <EmailValidityPill emailValidity={EmailValidity.VALID} />
          </Col>
          <Col label="INVALID">
            <EmailValidityPill emailValidity={EmailValidity.INVALID} />
          </Col>
          <Col label="CATCH_ALL">
            <EmailValidityPill emailValidity={EmailValidity.CATCH_ALL} />
          </Col>
          <Col label="UNKNOWN">
            <EmailValidityPill emailValidity={EmailValidity.UNKNOWN} />
          </Col>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// FbLink — React Router internal links
// ---------------------------------------------------------------------------
function FbLinkPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="React Router links">
        <Col>
          <FbLink to="/component-library">Component Library</FbLink>
          <FbLink to="/dashboard">Dashboard</FbLink>
          <FbLink to="/settings">Settings</FbLink>
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// OverflownText
// ---------------------------------------------------------------------------
function OverflownTextPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="maxLines=1">
        <div className="w-48">
          <OverflownText maxLines={1} className="text-sm text-fg">
            This long text will be clamped to exactly one line with an ellipsis
            at the end.
          </OverflownText>
        </div>
      </Section>
      <Section label="maxLines=2">
        <div className="w-48">
          <OverflownText maxLines={2} className="text-sm text-fg">
            This paragraph will clamp at two lines. Lorem ipsum dolor sit amet,
            consectetur adipiscing elit, sed do eiusmod tempor.
          </OverflownText>
        </div>
      </Section>
      <Section label="with tooltipText (hover when overflown)">
        <div className="w-48">
          <OverflownText
            maxLines={1}
            tooltipText="This is the full text shown in the tooltip when the content overflows its container width"
          >
            <span className="text-sm">
              This is the full text shown in the tooltip when the content
              overflows its container width
            </span>
          </OverflownText>
        </div>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// JobStatusPill — all numeric status values
// ---------------------------------------------------------------------------
function JobStatusPillPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="All status values">
        <Row>
          <Col label="0 — Submitted">
            <JobStatusPill statusValue={0} statusLabel="Submitted" />
          </Col>
          <Col label="10 — Started">
            <JobStatusPill statusValue={10} statusLabel="Started" />
          </Col>
          <Col label="20 — Pending">
            <JobStatusPill statusValue={20} statusLabel="Pending Approval" />
          </Col>
          <Col label="30 — Accepted">
            <JobStatusPill statusValue={30} statusLabel="Accepted" />
          </Col>
          <Col label="40 — Rejected">
            <JobStatusPill statusValue={40} statusLabel="Rejected" />
          </Col>
        </Row>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Checkbox (with indeterminate support)
// ---------------------------------------------------------------------------
function CheckboxPreview(): React.ReactElement {
  const [checked, setChecked] = useState(false)
  return (
    <Preview>
      <Section label="Interactive">
        <Row>
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked((e.target as HTMLInputElement).checked)}
          />
          <span className="text-sm text-fg-secondary">
            {checked ? 'Checked' : 'Unchecked'}
          </span>
        </Row>
      </Section>
      <Section label="All states (static)">
        <Col>
          <Row label="Unchecked">
            <Checkbox checked={false} readOnly />
          </Row>
          <Row label="Checked">
            <Checkbox checked={true} readOnly />
          </Row>
          <Row label="Indeterminate (partial selection)">
            <Checkbox indeterminate readOnly />
          </Row>
        </Col>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// TreeList
// ---------------------------------------------------------------------------
const treeNodes: TreeNode[] = [
  {
    id: 'root',
    label: 'Root category',
    subtitle: '12 items',
    defaultExpanded: true,
    children: [
      {
        id: 'child-1',
        label: 'Sub-category A',
        subtitle: '5 items',
        children: [
          { id: 'leaf-1', label: 'Item 1' },
          { id: 'leaf-2', label: 'Item 2' },
        ],
      },
      {
        id: 'child-2',
        label: 'Sub-category B',
        subtitle: '7 items',
        children: [{ id: 'leaf-3', label: 'Item 3' }],
      },
    ],
  },
]

function TreeListPreview(): React.ReactElement {
  return (
    <Preview>
      <Section label="Nested tree — expand/collapse nodes">
        <div className="w-full max-w-xs">
          <TreeList nodes={treeNodes} />
        </div>
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// FbUI/Tabs — styled-components tabs (no badges; isSmall, selectedColor, noGap)
// ---------------------------------------------------------------------------
export function FbUITabsPreview(): React.ReactElement {
  const [tab1, setTab1] = useState<
    'overview' | 'details' | 'activity' | 'disabled'
  >('overview')
  const [tab2, setTab2] = useState<'summary' | 'notes' | 'history'>('summary')
  const [tab3, setTab3] = useState<'active' | 'archive'>('active')
  return (
    <Preview>
      <Section label="Default — standard size + disabled tab">
        <FbUITabs
          tabs={[
            { key: 'overview', title: 'Overview' },
            { key: 'details', title: 'Details' },
            { key: 'activity', title: 'Activity' },
            { key: 'disabled', title: 'Disabled', disabled: true },
          ]}
          selectedTab={tab1}
          setSelectedTab={setTab1}
        />
      </Section>
      <Section label="isSmall — compact font + tighter padding">
        <FbUITabs
          tabs={[
            { key: 'summary', title: 'Summary' },
            { key: 'notes', title: 'Notes' },
            { key: 'history', title: 'History' },
          ]}
          selectedTab={tab2}
          setSelectedTab={setTab2}
          isSmall
        />
      </Section>
      <Section label="selectedColor — custom active-indicator color">
        <FbUITabs
          tabs={[
            { key: 'active', title: 'Active' },
            { key: 'archive', title: 'Archive' },
          ]}
          selectedTab={tab3}
          setSelectedTab={setTab3}
          selectedColor="#7c3aed"
        />
      </Section>
      <Section label="noGap — tabs flush (no 25px gap between items)">
        <FbUITabs
          tabs={[
            { key: 'a', title: 'Tab A' },
            { key: 'b', title: 'Tab B' },
            { key: 'c', title: 'Tab C' },
          ]}
          selectedTab="a"
          setSelectedTab={() => {}}
          noGap
        />
      </Section>
    </Preview>
  )
}

// ---------------------------------------------------------------------------
// Export map
// ---------------------------------------------------------------------------
export const tier2Previews: Record<string, () => React.ReactNode> = {
  FbButton: FbButtonPreview,
  TextPill: TextPillPreview,
  TagCountBadge: TagCountBadgePreview,
  EmailValidityPill: EmailValidityPillPreview,
  FbLink: FbLinkPreview,
  OverflownText: OverflownTextPreview,
  JobStatusPill: JobStatusPillPreview,
  Checkbox: CheckboxPreview,
  TreeList: TreeListPreview,
  // StagePill — requires API context (fetches sales stages from backend); intentionally no preview
  // Page (FBPage) — full-page layout; not suited for card preview
}
